import { isSodimacStandalone, isTottusStandalone } from '@digital-retail/store-manager';
import getConfig from 'next/config';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { getTenantValue } from '../../../containers/ProdctSpecifications/BuyTogether/constants';
import { isWindow } from '../../../utils/deviceChecker';
import { withLabels } from '../../../utils/LabelsContext';
import _ from '../../../utils/LodashImports';
import { noop } from '../../../utils/noop';
import { carouselActions, recoAnalytics } from '../../../utils/recoAnalytics';
import useIntersection from '../../../utils/useIntersection';
import useLazyLoadImage from '../../../utils/useLazyLoadImage';
import { getMeasurements } from '../../../utils/variant';
import { Badges } from '../../Badges/Badges';
import CartItemControls from '../../CartItemControls/CartItemControls';
import Prices from '../../Prices/Prices';
import { ShippingDetails } from '../../ShippingDetails/ShippingDetails';
import Button from '../../ui/Button/Button';
import Image from '../../ui/Image/Image';
import RatingsVisual from '../../ui/RatingsVisual/RatingsVisual';
import Reviews from '../../ui/Reviews/Reviews';
import { Actions, className, ProductsCarouselPodStyles, styles } from './ProductsCarouselPod.style';
import { getHref } from './utils';

const { publicRuntimeConfig } = getConfig();

const ProductsCarouselPod = ({
  product,
  appCtx,
  labels,
  details,
  aggregateViewedProducts,
  isSponsored,
  sponsoredClick,
}) => {
  const { deviceType, regionCode, store } = appCtx;
  const isDesktop = deviceType === 'desktop';
  const isMobile = deviceType === 'mobile';
  const [isButtonVisible, setIsButtonVisible] = useState(!isDesktop);
  const [loadImage, setLoadImage] = useState(false);
  const isTottusSA = isTottusStandalone(store);
  const productBrand = _.get(product, 'brand', '');
  const meatStickers = _.get(product, 'meatStickers', []);
  const isMoreThanOneRating = product.totalReviews && product.totalReviews > 0;
  const enableSponsoredInRecommendation = _.get(appCtx, 'siteConfig.toggles.enableSponsoredInRecommendation', false);
  const isEnabledAddToCartInReco = _.get(appCtx, 'siteConfig.toggles.isEnabledAddToCartInReco', false) && isTottusSA;
  const sponsoredText = _.get(labels, 'SPONSORED_TEXT', 'Patrocinado');
  const isProductSponsored = isSponsored || product?.isSponsored;
  const adType = details?.adType || 'carousel';
  const isRecommendedSponsored = enableSponsoredInRecommendation && product?.isSponsored;
  const href = getHref(product, details, isProductSponsored, publicRuntimeConfig, appCtx);
  const podRef = useRef();
  const inViewport = useIntersection(podRef, '0px', '0.3', false);
  const sendImpression = (isRecommendedSponsored || isSponsored) && inViewport && product?.adInfo;
  const hasSentImpression = useRef(false);
  const availabilityLabels = _.get(product, 'availabilityLabels', {});

  useEffect(() => {
    if (sendImpression && !hasSentImpression.current) {
      sponsoredClick(
        'impression',
        product.productId,
        product?.adInfo,
        details?.productPosition,
        product?.providerName,
        adType,
        product.productId
      );

      hasSentImpression.current = true;
    }
  }, [
    details?.productPosition,
    inViewport,
    isSponsored,
    product?.isSponsored,
    product?.adInfo,
    product.productId,
    product?.providerName,
    sendImpression,
    sponsoredClick,
    adType,
  ]);

  const productRef = React.useRef(null);
  const isCarouselInViewport = useIntersection(productRef, '0px', '1', false);
  const measurement = getMeasurements({
    product,
    productMeasurement: product.measurement,
  });

  if (isWindow() && aggregateViewedProducts) {
    if (isCarouselInViewport) {
      aggregateViewedProducts(product.id);
    }
  }
  useLazyLoadImage({ productRef, setLoadImage });

  const areSponsoredClickEventsEnabledWithQueryParams = _.get(
    appCtx,
    'siteConfig.toggles.areSponsoredClickEventsEnabledWithQueryParams',
    false
  );
  const shouldShowPatrocinadoPod =
    isRecommendedSponsored && ((isDesktop && !isButtonVisible) || (isMobile && isButtonVisible));

  const redirectToProduct = () => {
    const eventData = {
      carouselName: details.carouselName,
      currentProductID: details.parentVariantID,
      targetProductID: product.offeringId,
      numberProducts: details.numberProducts,
      productPosition: details.productPosition,
      carouselPosition: details.carouselPosition,
      country: getTenantValue(appCtx?.store, appCtx?.regionCode),
      store: appCtx.store,
    };

    if (isSponsored || isRecommendedSponsored) {
      if (!areSponsoredClickEventsEnabledWithQueryParams) {
        sponsoredClick(
          'click',
          product.productId,
          product.adInfo,
          details.productPosition,
          product?.providerName,
          adType,
          product.productId
        );
      }

      sponsoredClick(
        'visit',
        product.productId,
        product.adInfo,
        details.productPosition,
        product?.providerName,
        adType
      );
    } else if (isSodimacStandalone(appCtx.store)) {
      recoAnalytics.click({
        carouselKey: 'CR_RecommendationCarousel',
        siteName: getTenantValue(appCtx?.store, appCtx?.regionCode),
        action: carouselActions.clickToRate,
        carouselName: details.carouselName,
        skuId: product.id,
        podPositionInCarousel: details.productPosition,
        lengthPodsInCarousel: details.numberProducts,
        slotId: details.slotId,
      });
      return;
    }
    const eventToDigitalData = new CustomEvent('DDPDPCarouselProductClick', {
      bubbles: true,
      detail: eventData,
    });
    window.dispatchEvent(eventToDigitalData);
  };
  const imageSrc = product.mediaUrls && product.mediaUrls[0];

  const actions = isEnabledAddToCartInReco && (
    <div className="pod-actions">
      <CartItemControls product={product} carouselId={details.carouselId} carouselName={details.carouselName} />
      <style jsx>{Actions}</style>
    </div>
  );

  const imagePod = (image) =>
    isEnabledAddToCartInReco ? (
      <a onClick={redirectToProduct} href={href}>
        {image}
      </a>
    ) : (
      image
    );

  const carousel = (
    <div className="products-carousel-pod">
      <div className={`products-carousel-pod-layout ${isTottusSA ? className : ''}`}>
        <div className="image-container">
          <div className="image-wrapper">
            {loadImage &&
              imagePod(
                <Image
                  id={product.id}
                  width={136}
                  height={136}
                  alt={product.displayName}
                  useSrcSet={false}
                  inlineDimensions={false}
                  imageUrl={imageSrc}
                  lazyLoad
                />
              )}
          </div>
        </div>
        <div className="pod-details">
          <div className="pod-product-brand">{productBrand}</div>
          <div className={`pod-product-description ${isSponsored && 'pod-sponsored-product-description'}`}>
            {product.displayName}
          </div>
          <div className={`pod-product-price ${isSponsored && 'pod-sponsored-product-price'}`}>
            <Prices
              priceList={product.prices}
              productId={product.productId}
              page="POD"
              layout="4_GRID"
              variant="DESKTOP_4_GRID"
              discountBadge={product.discountBadge}
              isSponsoredProduct={isSponsored}
              measurement={measurement}
              regionCode={regionCode}
            />
          </div>
          {isMoreThanOneRating && (
            <div className="pod-rating">
              <RatingsVisual value={Number(product.rating)} productId={product.productId} size="normal" />
              <Reviews value={Number(product.totalReviews)} />
            </div>
          )}
        </div>
        {isTottusSA && Object.keys(availabilityLabels).length > 0 && (
          <ShippingDetails availabilityLabels={availabilityLabels} />
        )}
      </div>
      <div className={`patrocinado-pod ${shouldShowPatrocinadoPod ? '' : 'hidden'}`}>
        {isRecommendedSponsored && <div className="sponsored-product-text">{sponsoredText}</div>}
      </div>
      {actions}
      <style jsx>{ProductsCarouselPodStyles}</style>
      {styles}
    </div>
  );

  const linkToProduct = (children) => (
    <div
      onMouseEnter={() => isDesktop && setIsButtonVisible(true)}
      onMouseLeave={() => isDesktop && setIsButtonVisible(false)}
      className={`products-carousel-pod-wrapper ${isSponsored && 'sponsored-products-carousel-pod-wrapper'}`}
      ref={productRef}
    >
      {meatStickers && <Badges badgeItems={meatStickers} deviceType={deviceType} layout="PDP POD" isSticker />}
      <a onClick={redirectToProduct} className="pod-anchor" href={href} aria-hidden="true" ref={podRef}>
        {children}
        {(!isMobile || (isSponsored && isMobile)) && (
          <div
            className={`pod-button ${isSponsored && isMobile && 'sponsored-pod-button'}`}
            style={{
              visibility: isButtonVisible || (isSponsored && isMobile) ? 'visible' : 'hidden',
            }}
          >
            {isSponsored ? (
              <Button type="mkp-quatinery generic-outlined" strech="full" size="small">
                {labels.ADD_TO_CART}
              </Button>
            ) : (
              <Button type="mkp-secondary generic-outlined">{labels.ADD_TO_CART}</Button>
            )}
          </div>
        )}
      </a>
      <style jsx>{ProductsCarouselPodStyles}</style>
    </div>
  );

  const addToCart = (children) => (
    <div
      className={`products-carousel-pod-wrapper ${isSponsored && 'sponsored-products-carousel-pod-wrapper'}`}
      ref={productRef}
    >
      {meatStickers && <Badges badgeItems={meatStickers} deviceType={deviceType} layout="PDP POD" isSticker />}
      {children}
      <style jsx>{ProductsCarouselPodStyles}</style>
    </div>
  );

  return (
    <>
      {isEnabledAddToCartInReco ? addToCart(carousel) : linkToProduct(carousel)}
      <style jsx>{ProductsCarouselPodStyles}</style>
    </>
  );
};

ProductsCarouselPod.propTypes = {
  product: PropTypes.object.isRequired,
  appCtx: PropTypes.object.isRequired,
  labels: PropTypes.object,
  details: PropTypes.object.isRequired,
  aggregateViewedProducts: PropTypes.func.isRequired,
  isSponsored: PropTypes.bool,
  sponsoredClick: PropTypes.func,
};

ProductsCarouselPod.defaultProps = {
  labels: { ADD_TO_CART: 'Ver producto' },
  isSponsored: false,
  sponsoredClick: noop,
};

export { ProductsCarouselPod };
export default withLabels(ProductsCarouselPod);
